import { useState, useEffect, useCallback } from 'react';
import fishService from '../services/fishService.js';
import { fishDataFromCsv } from '../data/fishDataFromCsv.js';

// Custom hook for fish data management
export const useFish = () => {
  const [fish, setFish] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFish = useCallback(async () => {
    try {
      setLoading(true);
      const fishData = await fishService.getAllFish();
      setFish(fishData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFish();
  }, [loadFish]);

  const addFish = async (fishData) => {
    try {
      const newId = await fishService.addFish(fishData);
      const newFish = { id: newId, ...fishData };
      setFish(prev => [...prev, newFish]);
      return newId;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateFish = async (id, fishData) => {
    try {
      await fishService.updateFish(id, fishData);
      setFish(prev => // Update fish state
        prev.map(f => f.id === id ? { ...f, ...fishData } : f) // Map over previous state to update the specific fish
      );
    } catch (err) { // Catch any errors
      setError(err.message); // Set error message
      throw err; // Re-throw the error
    }
  };

  const deleteFish = async (id) => {
    try {
      await fishService.deleteFish(id);
      setFish(prev => prev.filter(f => f.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    fish,
    loading,
    error,
    addFish,
    updateFish,
    deleteFish,
    refetch: loadFish
  };
};

// Hook for single fish
export const useSingleFish = (fishId) => {
  const [fish, setFish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fishId) return;

    const loadFish = async () => {
      try {
        setLoading(true);
        const fishData = await fishService.getFishById(fishId);
        setFish(fishData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFish();
  }, [fishId]);

  return { fish, loading, error };
};

// Hook for filtered fish
export const useFilteredFish = (filters = {}) => {
  const [fish, setFish] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFilteredFish = async () => {
      try {
        setLoading(true);
        let fishData = [];

        try {
          if (filters.category) {
            fishData = await fishService.getFishByCategory(filters.category);
          } else if (filters.careLevel) {
            fishData = await fishService.getFishByCareLevel(filters.careLevel);
          } else if (filters.availableOnly) {
            fishData = await fishService.getAvailableFish();
          } else {
            fishData = await fishService.getAllFish();
          }
        } catch (apiError) {
          console.warn('Falling back to local fish data for filtered fish. API error:', apiError?.message || apiError);
          fishData = fishDataFromCsv;
        }

        // Apply additional client-side filters
        if (filters.priceRange) {
          const [min, max] = filters.priceRange;
          fishData = fishData.filter(f => f.price >= min && f.price <= max);
        }

        if (filters.reefSafe !== undefined) {
          fishData = fishData.filter(f => f.reef_safe === filters.reefSafe);
        }

        if (filters.temperament) {
          fishData = fishData.filter(f => 
            f.temperament.toLowerCase().includes(filters.temperament.toLowerCase())
          );
        }

        if (filters.searchTerm) {
          const term = filters.searchTerm.toLowerCase();
          fishData = fishData.filter(f => 
            f.name.toLowerCase().includes(term) ||
            f.scientific_name.toLowerCase().includes(term) ||
            f.description.toLowerCase().includes(term) ||
            (f.search_keywords && f.search_keywords.some(keyword => 
              keyword.includes(term)
            ))
          );
        }

        setFish(fishData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFilteredFish();
  }, [JSON.stringify(filters)]);

  return { fish, loading, error };
};

// Hook for real-time fish updates
export const useRealtimeFish = () => {
  const [fish, setFish] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = fishService.subscribeToFishUpdates((fishData) => {
      setFish(fishData);
      setLoading(false);
    });

    // Error handling for real-time updates
    const handleError = (err) => {
      setError(err.message);
      setLoading(false);
    };

    return () => {
      unsubscribe();
    };
  }, []);

  return { fish, loading, error };
};
